import { useEffect, useId, useState, type ReactNode } from 'react';
import { Maximize2, Minimize2, X } from 'lucide-react';
import { IconButton } from '../private/PrivatePrimitives';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  allowFullscreen?: boolean;
}

export default function Modal({ isOpen, onClose, title, children, size = 'md', allowFullscreen = false }: Props) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const titleId = useId();

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape' && isOpen) onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => { if (!isOpen) setIsFullscreen(false); }, [isOpen]);

  if (!isOpen) return null;
  const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-5xl' };

  return (
    <div className={`fixed inset-0 z-50 ${isFullscreen ? '' : 'flex items-center justify-center p-4'}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <button type="button" className="fixed inset-0 bg-black/50" onClick={onClose} aria-label="Close dialog" />
      <div className={`relative flex flex-col overflow-hidden border border-app-line bg-app-surface-raised shadow-[0_24px_64px_rgba(15,16,17,0.22)] ${isFullscreen ? 'h-full w-full' : `max-h-[88vh] w-full rounded-lg ${widths[size]}`}`}>
        <div className="flex shrink-0 items-center justify-between border-b border-app-line px-5 py-4">
          <h2 id={titleId} className="text-base font-semibold text-app-ink">{title}</h2>
          <div className="flex items-center gap-2">
            {allowFullscreen ? (
              <IconButton label={isFullscreen ? 'Exit fullscreen' : 'Open fullscreen'} onClick={() => setIsFullscreen(value => !value)}>
                {isFullscreen ? <Minimize2 className="h-4 w-4" aria-hidden="true" /> : <Maximize2 className="h-4 w-4" aria-hidden="true" />}
              </IconButton>
            ) : null}
            <IconButton label="Close dialog" onClick={onClose}><X className="h-4 w-4" aria-hidden="true" /></IconButton>
          </div>
        </div>
        <div className={`flex-1 overflow-y-auto ${isFullscreen ? 'px-8 py-6' : 'p-5'}`}>{children}</div>
      </div>
    </div>
  );
}
